import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Extintores de incendio (portátiles y móviles).
 *
 * Referencias normativas:
 *   - RD 513/2017 (Reglamento de Instalaciones de Protección contra Incendios), Anexo II, Tabla I.
 *   - UNE 23120 (mantenimiento de extintores) / UNE-EN 3.
 *   - ITC-MIE-AP5 (retimbrado / prueba hidráulica cada 5 años, máximo 3 retimbres → vida útil 20 años).
 *
 * Periodicidad:
 *   T = Trimestral (usuario / titular)
 *   A = Anual (empresa mantenedora)
 *   5A = Quinquenal (retimbrado / prueba hidráulica)
 */
export const EXTINTORES_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'extintores',
  titulo: 'Sistema de Extintores de Incendio',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y estado general',
      cabecera: [
        { key: 'cantidad_total', label: 'Nº total de extintores', tipo: 'number' },
        { key: 'cantidad_polvo', label: 'Polvo ABC', tipo: 'number' },
        { key: 'cantidad_co2', label: 'CO₂', tipo: 'number' },
        { key: 'cantidad_agua', label: 'Agua', tipo: 'number' },
        { key: 'cantidad_espuma', label: 'Espuma', tipo: 'number' },
        { key: 'cantidad_k', label: 'Clase K', tipo: 'number' },
        { key: 'cantidad_moviles', label: 'Móviles / carro', tipo: 'number' },
      ],
      preguntas: [
        { id: 'EX.00', texto: '¿Dispone cada extintor de la etiqueta de revisión por empresa mantenedora en correcto estado, visible y legible?', periodicidad: ['T','A'], unidades: true },
        { id: 'EX.01', texto: '¿Tienen los extintores marcado CE y placa de diseño / contraseña de homologación visible?', periodicidad: ['A'], unidades: true },
        { id: 'EX.02', texto: '¿Coincide el inventario físico con el último plano / listado de extintores del cliente?', periodicidad: ['A'] },
        { id: 'EX.03', texto: '¿Se han producido bajas, altas o reubicaciones desde la última revisión y están documentadas?', periodicidad: ['A'] },
        { id: 'EX.04', texto: '¿La distribución (un extintor a 15 m máximo en recorrido real desde cualquier origen de evacuación) cumple lo exigido por el RD 513/2017?', periodicidad: ['A'] },
        { id: 'EX.05', texto: '¿Existen extintores específicos junto a riesgos especiales (cuadros eléctricos, cocinas con freidoras, almacenes de líquidos inflamables, salas técnicas)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'ubicacion',
      titulo: '2. Ubicación, accesibilidad y señalización',
      preguntas: [
        { id: 'UB.01', texto: '¿Están los extintores correctamente colgados de paramento vertical mediante soporte adecuado, o sobre carro si son móviles?', periodicidad: ['T','A'], unidades: true },
        { id: 'UB.02', texto: '¿La parte superior del extintor está a una altura ≤ 1,70 m del suelo?', periodicidad: ['T','A'], unidades: true },
        { id: 'UB.03', texto: '¿Son fácilmente visibles y accesibles, libres de obstáculos y próximos a salidas o recorridos de evacuación?', periodicidad: ['T','A'], unidades: true },
        { id: 'UB.04', texto: '¿Cuenta cada extintor con su señalización fotoluminiscente normalizada (UNE 23033 / UNE 23035) visible incluso con humo?', periodicidad: ['T','A'], unidades: true },
        { id: 'UB.05', texto: '¿Están protegidos de la intemperie, golpes y temperaturas fuera del rango -5 °C a +60 °C (o el indicado por fabricante)?', periodicidad: ['A'], unidades: true },
        { id: 'UB.06', texto: 'Los extintores en armario, ¿permiten su uso inmediato sin necesidad de llaves ni herramientas?', periodicidad: ['T','A'] },
      ],
    },
    {
      id: 'estado_extintor',
      titulo: '3. Estado físico del extintor',
      preguntas: [
        { id: 'ES.01', texto: '¿El estado externo del cuerpo del extintor es correcto (sin abolladuras, oxidación, corrosión ni pintura saltada)?', periodicidad: ['T','A'], unidades: true },
        { id: 'ES.02', texto: '¿El precinto y el pasador de seguridad están en perfecto estado y sin signos de manipulación?', periodicidad: ['T','A'], unidades: true },
        { id: 'ES.03', texto: '¿La maneta de accionamiento y la palanca están en buen estado y se accionan correctamente?', periodicidad: ['A'], unidades: true },
        { id: 'ES.04', texto: '¿La manguera, lanza/boquilla y difusor (en CO₂) están en buen estado, sin grietas ni obstrucciones?', periodicidad: ['T','A'], unidades: true },
        { id: 'ES.05', texto: '¿La etiqueta de instrucciones de uso está en castellano, completa y legible?', periodicidad: ['T','A'], unidades: true },
        { id: 'ES.06', texto: '¿El soporte mural o carro está bien fijado y no presenta deterioro?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'presion_carga',
      titulo: '4. Presión, carga y peso (mantenimiento anual)',
      cabecera: [
        { key: 'metodo_pesada', label: 'Método de comprobación de carga' },
        { key: 'manometro_patron', label: 'Manómetro patrón (Nº)' },
        { key: 'fecha_calibracion_patron', label: 'Calibración manómetro patrón', tipo: 'date' },
      ],
      preguntas: [
        { id: 'PC.01', texto: 'En extintores con manómetro (polvo, agua, espuma), ¿la aguja está en zona verde y dentro de los márgenes admisibles?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.02', texto: '¿Se ha comprobado la presión interna con manómetro patrón calibrado y coincide con la indicada por el manómetro propio?', periodicidad: ['A'], unidades: true },
        { id: 'PC.03', texto: 'En extintores de CO₂, ¿se ha comprobado el peso de la carga y la pérdida es < 5 % o la indicada por el fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'PC.04', texto: '¿Se ha verificado el peso total del extintor y se ha registrado en la etiqueta de mantenimiento?', periodicidad: ['A'], unidades: true },
        { id: 'PC.05', texto: 'En extintores con botellín de impulsión, ¿se ha pesado el botellín y se han verificado los conjuntos cierre y disparo?', periodicidad: ['A'], unidades: true },
        { id: 'PC.06', texto: '¿Se ha comprobado el estado del agente extintor (aglutinación de polvo, fluidez, fecha de caducidad del agente espumante)?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'retimbrado',
      titulo: '5. Retimbrado y vida útil',
      preguntas: [
        { id: 'RT.01', texto: '¿Todos los extintores están dentro del plazo de retimbrado (5 años desde fabricación o último retimbre)?', periodicidad: ['A'], unidades: true },
        { id: 'RT.02', texto: '¿La prueba hidráulica del último retimbre cuenta con certificado expedido por organismo competente y placa/etiqueta visible?', periodicidad: ['A'], unidades: true },
        { id: 'RT.03', texto: '¿Ningún extintor supera los 20 años desde su fecha de fabricación (máximo 3 retimbres)?', periodicidad: ['A'], unidades: true, nota: 'A los 20 años el extintor debe ser dado de baja y sustituido (ITC-MIE-AP5).' },
        { id: 'RT.04', texto: '¿Se ha grabado en el cuerpo del extintor la fecha de retimbrado y el contraste del organismo de control?', periodicidad: ['A'], unidades: true },
        { id: 'RT.05', texto: '¿Se han retirado del servicio los extintores caducados, dañados irreparablemente o sin homologación vigente?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '6. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión anual realizada por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Se conservan los certificados de retimbrado de los últimos 5 años?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Está actualizado el listado / inventario con marca, modelo, ubicación, nº de serie, fechas de fabricación, retimbre y caducidad de cada extintor?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Dispone el cliente del plan de mantenimiento y registro de incidencias / sustituciones del último año?', periodicidad: ['A'] },
        { id: 'DC.05', texto: '¿Se ha entregado al titular el informe con las anomalías detectadas y las acciones correctoras propuestas?', periodicidad: ['A'] },
      ],
    },
  ],
};
